"use client";

import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { Section } from "@/components/ui/section";
import { ProjectCard } from "@/components/ui/project-card";
import { BackgroundLines } from "@/components/ui/background-lines";
import { DesktopView } from "@/components/apps-os/DesktopView";
import { MobileOSView } from "@/components/apps-os/MobileOSView";
import { PROJECTS_DATA, projectToApp } from "@/lib/data";
import { m as motion } from "framer-motion";
import { LayoutGrid, Smartphone } from "lucide-react";

import { ShippedCard } from "@/components/projects/ShippedCard";
import { AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import BlogFooter from "@/components/blog/BlogFooter";

interface ProjectsPageProps {
  projectsData?: any[];
}

export default function ProjectsPageClient({
  projectsData,
}: ProjectsPageProps) {
  const searchParams = useSearchParams();
  const [view, setView] = useState<"projects" | "apps">("apps");
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [selectedProjectForModal, setSelectedProjectForModal] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  // Sync with Navbar toggle
  useEffect(() => {
    const handleViewToggle = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail !== view) {
        setView(customEvent.detail as "projects" | "apps");
      }
    };

    window.addEventListener("toggleProjectsView", handleViewToggle);
    return () =>
      window.removeEventListener("toggleProjectsView", handleViewToggle);
  }, [view]);

  // Use database data or fall back to static data
  const projects =
    projectsData && projectsData.length > 0 ? projectsData : PROJECTS_DATA;

  // Filter projects for the projects page
  const projectsPageData = useMemo(
    () =>
      projects.filter(
        (project: any) => project.isVisibleInProjectsPage !== false
      ),
    [projects]
  );

  // Convert projects to apps for OS view
  const appsData = useMemo(
    () =>
      projects
        .filter((project: any) => project.isVisibleInAppOS === true)
        .map((project: any) => projectToApp(project)),
    [projects]
  );
  
  // Mapper to transform Project data into ShippedCard expected format
  const projectToShippedApp = (project: any, index: number) => ({
    id: project.id || `project-${index}`,
    name: project.title,
    description: project.description,
    year: project.year,
    techStack: project.tags,
    blogSlug: project.blogSlug || project.link?.split('/').pop() || null,
    githubUrl: project.githubUrl,
    liveUrl: project.liveUrl,
    imageUrl: project.image || project.logo || (project.images?.[0]) || null,
    screenshots: project.images || [],
    type: project.type || "app",
  });

  // Hydration fix - only render state-dependent UI after mount
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (selectedProjectForModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedProjectForModal]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Check for app query parameter and open that app - ONLY for Apps OS view
  useEffect(() => {
    const appId = searchParams.get("app");
    if (appId && view === "apps") {
      const project = projects.find((p: any) => p.id === appId);
      if (project) {
        const appData = projectToApp(project);
        setSelectedApp(appData);
        setSelectedProjectForModal(null);
      }
    }
  }, [searchParams, projects, view]);

  return (
    <main className="relative overflow-hidden min-h-screen">
      <BackgroundLines />

      {/* Main Content */}
      <div className={!isHydrated ? "pt-0" : cn(
        "flex flex-col z-10",
        view === "apps" 
          ? "fixed inset-0 overflow-y-auto scrollbar-hide pt-4 lg:pt-48 pb-24 lg:pb-8" 
          : "relative min-h-screen pt-24 lg:pt-48 pb-20 md:pb-0 overflow-visible"
      )}>
        
        {/* Global View Switcher - Premium Pill Style at the Top */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="fixed top-8 md:top-10 left-0 right-0 z-[50] justify-center pointer-events-none hidden md:flex"
        >
          <div className="pointer-events-auto bg-white/70 dark:bg-black/40 backdrop-blur-xl p-1.5 rounded-2xl border border-black/5 dark:border-white/10 flex items-center gap-1 shadow-[0_20px_50px_rgba(0,0,0,0.1)] dark:shadow-2xl">
            <button
              onClick={() => setView("apps")}
              className={cn(
                "flex items-center gap-3 px-8 py-3 rounded-xl text-sm font-bold transition-all duration-300",
                view === "apps"
                  ? "bg-black dark:bg-white text-white dark:text-black shadow-lg shadow-black/10 dark:shadow-white/10 scale-[1.02]"
                  : "text-zinc-500 dark:text-zinc-400 hover:text-black dark:hover:text-white"
              )}
            >
              <Smartphone className={cn("w-4 h-4 transition-colors", view === "apps" ? "text-white dark:text-black" : "text-zinc-500 dark:text-zinc-400")} />
              Apps OS
            </button>
            <button
              onClick={() => setView("projects")}
              className={cn(
                "flex items-center gap-3 px-8 py-3 rounded-xl text-sm font-bold transition-all duration-300",
                view === "projects"
                  ? "bg-black dark:bg-white text-white dark:text-black shadow-lg shadow-black/10 dark:shadow-white/10 scale-[1.02]"
                  : "text-zinc-500 dark:text-zinc-400 hover:text-black dark:hover:text-white"
              )}
            >
              <LayoutGrid className={cn("w-4 h-4 transition-colors", view === "projects" ? "text-white dark:text-black" : "text-zinc-500 dark:text-zinc-400")} />
              Grid View
            </button>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {view === "apps" ? (
            <motion.div
              key="os-view"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="flex-1 w-full relative"
            >
              {isHydrated && (
                <div className="h-full w-full flex flex-col">
                  {/* Mobile View - Visible on screens smaller than lg */}
                  <div className="block lg:hidden flex-1 w-full relative flex items-center justify-center p-2 sm:p-4 max-h-full overflow-hidden">
                    {/* Phone Frame - Height Constrained */}
                    <div className="relative h-full max-h-[88vh] w-auto min-w-[240px] aspect-[9/18.5] bg-gradient-to-br from-gray-900 to-black rounded-[2.5rem] p-2 dark:p-2.5 shadow-2xl border-[3px] border-gray-800 ring-1 ring-white/10 flex flex-col shrink-0 transform-gpu">
                      {/* Notch */}
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-6 bg-black rounded-b-2xl z-20 pointer-events-none" />
                      
                      {/* Screen Content - Strictly Clipped */}
                      <div className="relative w-full h-full bg-background rounded-[2rem] overflow-hidden isolate transform-gpu [container-type:inline-size]">
                        <MobileOSView
                          apps={appsData}
                          onAppLaunch={(app) => {
                            setSelectedApp(app);
                          }}
                          currentApp={selectedApp}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Desktop View - Visible on lg screens and up */}
                  <div className="hidden lg:block min-h-full w-full">
                    <DesktopView
                      apps={appsData}
                      selectedApp={selectedApp}
                      setSelectedApp={(app) => {
                        setSelectedApp(app);
                      }}
                    />
                  </div>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="feed-view"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Projects Page Header - Only in Grid View */}
              <Section className="!pb-6 !pt-6 md:!pt-12">
                <div className="text-center max-w-4xl mx-auto space-y-3 sm:space-y-4 px-2">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="inline-flex items-center gap-1 sm:gap-2"
                  >
                    <span className="text-xs sm:text-lg text-muted-foreground font-light">
                      Built with Passion
                    </span>
                    <div className="w-1.5 h-1.5 sm:w-2.5 sm:h-2.5 rounded-full bg-primary animate-pulse" />
                  </motion.div>

                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-3xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-none bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent px-2"
                  >
                    All Projects
                  </motion.h1>

                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-[11px] sm:text-lg font-medium tracking-tight leading-relaxed max-w-2xl mx-auto px-4 text-muted-foreground/80"
                  >
                    A showcase of my work in mobile and web development.
                  </motion.p>
                </div>
              </Section>

              <Section className="!py-0">
                <div className="space-y-6 md:space-y-12 pb-24 md:pb-32 px-2 sm:px-0">
                    {/* Projects Feed - Slim Single Column */}
                    <div className="flex flex-col gap-4 sm:gap-8 max-w-5xl mx-auto">
                      {projectsPageData && projectsPageData.length > 0 ? (
                        projectsPageData.map((project: any, index: number) => (
                          <ShippedCard 
                            key={project.id || index} 
                            app={projectToShippedApp(project, index)} 
                            delay={index * 0.05} 
                            isFullWidth={false}
                            onClick={() => setSelectedProjectForModal(project)}
                          />
                        ))
                      ) : projectsPageData.length === 0 && (
                        <div className="col-span-full text-center py-20 bg-secondary/20 rounded-2xl border border-dashed border-border">
                          <p className="text-muted-foreground italic">No projects found</p>
                        </div>
                      )}
                    </div>
                </div>
              </Section>
              <BlogFooter />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {selectedProjectForModal && (
            <div key="project-modal" className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-4 pt-12 md:p-8 overscroll-behavior-contain">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                onClick={() => setSelectedProjectForModal(null)}
                className="absolute inset-0 bg-black/60 dark:bg-black/90 backdrop-blur-xl cursor-zoom-out"
              />
              
              <motion.div
                initial={{ opacity: 0, scale: 0.98, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: 10 }}
                transition={{ 
                  type: "spring", 
                  damping: 30, 
                  stiffness: 300,
                }}
                className="relative w-full max-w-6xl z-10 max-h-[70vh] overflow-y-auto overflow-x-hidden rounded-3xl border border-black/5 dark:border-white/10 bg-white dark:bg-[#050505] shadow-[0_0_50px_rgba(0,0,0,0.1)] dark:shadow-[0_0_50px_rgba(0,0,0,0.5)] scrollbar-hide overscroll-contain"
              >
                <div className="p-0">
                  <ShippedCard 
                    app={projectToShippedApp(selectedProjectForModal, 0)} 
                    isFullWidth={true} 
                  />
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}
